import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Collapse, Paper, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import PanelBlock, { PanelEyebrow } from '../PanelBlock';
import ConsistencySection from './ConsistencySection';
import SectionAssessment from './SectionAssessment';
import { getLaboklinApi } from '../../../services/laboklinApi';
import { LABOKLIN_BORDER, LABOKLIN_RED } from '../../../theme/laboklinTheme';
import type { Assessment, AssessmentSection, ReportDetail } from '../../../types/labConsistency';

/**
 * One report, five runs, three graded sections.
 *
 * The verdict comes first, because it decides whether the sections below it mean
 * anything: a contaminated sample must produce no diagnoses, so an empty differential
 * is either the right answer or a defect, and only the verdict says which.
 */

export const ConsistencyReportView: React.FC = () => {
  const { batchId, reportNo } = useParams<{ batchId: string; reportNo: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showWhy, setShowWhy] = useState(false);

  useEffect(() => {
    if (!batchId || !reportNo) return;
    let live = true;
    getLaboklinApi()
      .getConsistencyReport(batchId, reportNo)
      .then((d) => live && setDetail(d))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [batchId, reportNo]);

  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!detail) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>;

  const { report, runs, step } = detail;
  const byId = new Map<AssessmentSection, Assessment>();
  for (const a of detail.assessments) byId.set(a.section, a);

  const refused = runs.filter((r) => r.outcome === 'notDiagnostic');
  const split = refused.length > 0 && refused.length < runs.length;
  const allRefused = refused.length === runs.length && runs.length > 0;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Button size="small" onClick={() => navigate(`/mylab2/consistency-reports/${batchId}`)} sx={{ textTransform: 'none', mb: 1.5 }}>
        ← All reports
      </Button>
      <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{report.reportNo}</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: report.failedRuns > 0 ? 1 : 2 }}>
        {report.runs} identical runs · {step === 'basic' ? 'clinical history withheld' : 'as MyLab shows it'}
      </Typography>
      {/*
        Said before any score, because it changes what the scores below mean. A run that
        produced no case has no panels to compare and is not scored, so the agreement
        figures describe only the runs that survived - and their agreeing with each other
        says nothing about the ones that did not.
      */}
      {report.failedRuns > 0 && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: 13 }}>
          {report.failedRuns} of {report.runs} runs produced no case at all, so the scores below
          describe the remaining {report.runs - report.failedRuns}. Whether opening a case was the
          right call here is itself worth your view.
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: `1px solid ${LABOKLIN_BORDER}`, borderRadius: 1 }}>
        {/*
          Stated in words rather than as a percentage. A split verdict is the most
          serious thing this report can carry: both answers reached a veterinarian as
          fact, and one of them was a differential built on values the laboratory itself
          called uninterpretable.
        */}
        <PanelBlock>
          <PanelEyebrow
            label="Verdict"
            state={split ? `${report.verdict.diagnostic}/${report.verdict.runs} diagnostic` : `${report.verdict.runs}/${report.verdict.runs} agree`}
          />
          {split ? (
            <>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: LABOKLIN_RED, mb: 0.75 }}>
                The runs disagreed about whether this sample can be diagnosed at all.
              </Typography>
              <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                {report.verdict.notDiagnostic} of {report.verdict.runs} runs judged it not diagnostic;
                the other {report.verdict.diagnostic} produced a differential from the same report.
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 13 }}>
              {allRefused
                ? 'Every run judged this sample not diagnostic and produced no differential — which is the required behaviour for a contaminated or impossible sample.'
                : 'Every run produced a differential.'}
            </Typography>
          )}
          {report.verdict.reasons.map((reason) => (
            <Typography key={reason} sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75, pl: 1.25, borderLeft: `2px solid ${LABOKLIN_BORDER}` }}>
              {reason}
            </Typography>
          ))}
        </PanelBlock>

        {/*
          Nothing to grade when every run correctly refused: showing three empty
          sections would invite a veterinarian to score a correct refusal as a failure.
        */}
        {allRefused ? (
          <PanelBlock>
            <PanelEyebrow label="Sections" state="Not applicable" />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              No differential, exclusions or follow-up tests were produced, and none should have been.
            </Typography>
            <SectionAssessment
              batchId={report.batchId}
              reportNo={report.reportNo}
              step={step}
              section="overall"
              contentHash={report.contentHash}
              existing={byId.get('overall')}
            />
          </PanelBlock>
        ) : (
          <>
            <ConsistencySection
              section="differentials"
              stability={report.differentials}
              engine={report.engineDifferentials}
              runs={runs}
              report={report}
              step={step}
              assessment={byId.get('differentials')}
              emptyMessage="No differential was produced in any run."
              proseFor={(run, id) =>
                run.differentials.find((d) => d.diagnosisId === id)?.summary ?? null
              }
              rankFor={(run, id) => run.differentials.find((d) => d.diagnosisId === id)?.rank ?? null}
            />

            <ConsistencySection
              section="setAside"
              stability={report.setAside}
              engine={report.engineSetAside}
              runs={runs}
              report={report}
              step={step}
              assessment={byId.get('setAside')}
              emptyMessage="Nothing was set aside in any run — which is a valid answer when nothing is contradicted."
              proseFor={(run, id) => {
                const entry = run.setAside.find((d) => d.diagnosisId === id);
                if (!entry) return null;
                return [entry.reason, entry.whatWouldSettleIt].filter(Boolean).join(' ');
              }}
            />

            <ConsistencySection
              section="tests"
              stability={report.tests}
              runs={runs}
              report={report}
              step={step}
              assessment={byId.get('tests')}
              emptyMessage="No follow-up tests were offered in any run."
              proseFor={(run, id) => {
                const t = run.tests.find((x) => x.serviceNo === id);
                if (!t) return null;
                return [t.settles, t.price !== null ? `EUR ${t.price.toFixed(2)}` : null]
                  .filter(Boolean)
                  .join(' · ');
              }}
            />

            {/*
              Next steps carry no ids, so they cannot be set-compared. Saying so beats
              implying a precision the measurement does not have.
            */}
            <PanelBlock>
              <PanelEyebrow label="What to do next" state="Presence and count only" />
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
                These are free prose with no identifiers, so they are not scored for agreement.
              </Typography>
              {runs.map((run) => (
                <Box key={run.run} sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', fontWeight: 600 }}>
                    Run {run.run} · {run.nextSteps.length} step{run.nextSteps.length === 1 ? '' : 's'}
                  </Typography>
                  {run.nextSteps.map((s, i) => (
                    <Typography key={i} sx={{ fontSize: 13, lineHeight: 1.55 }}>• {s}</Typography>
                  ))}
                </Box>
              ))}
            </PanelBlock>
          </>
        )}

        {/*
          Off by default. A veterinarian is grading three panels, and an extracted
          symptom list is machinery rather than an answer - but it settles the one
          question the panel numbers cannot, which is what to blame when the ENGINE's
          own ranking is what moved.
        */}
        <PanelBlock>
          <Button size="small" onClick={() => setShowWhy(!showWhy)} sx={{ textTransform: 'none', fontSize: 12 }}>
            {showWhy ? 'Hide' : 'Why did this vary?'}
          </Button>
          <Collapse in={showWhy} unmountOnExit>
            <Box sx={{ mt: 1 }}>
              {runs.map((run) => (
                <Box key={run.run} sx={{ mb: 1.25 }}>
                  <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', fontWeight: 600 }}>
                    Run {run.run}
                  </Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    <strong>Symptoms fed to the engine:</strong>{' '}
                    {run.symptoms.map((s) => s.name).join(', ') || '(none)'}
                  </Typography>
                  <Typography sx={{ fontSize: 12 }}>
                    <strong>Engine ranked:</strong>{' '}
                    {run.engineDiagnoses.map((d) => d.name).join(', ') || '(none)'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </PanelBlock>
      </Paper>
    </Box>
  );
};

export default ConsistencyReportView;
